<template>
  <div class="home-wrapper">
    <!-- Loading -->
    <div v-if="isLoading" class="loading-screen">
      <v-progress-circular size="64" width="5" indeterminate color="#3f51b5"></v-progress-circular>
      <p class="loading-text">Chargement...</p>
    </div>

    <!-- App -->
    <div v-else>
      <!-- Top bar -->
      <div class="top-bar">
        <div class="top-bar-brand">
          <v-icon color="#3f51b5" size="24">mdi-calendar-check</v-icon>
          <span class="brand-name">ACCEN</span>
          <span class="brand-sep">|</span>
          <span class="brand-sub">Gestion des congés</span>
        </div>

        <!-- Mode toggle — admins uniquement -->
        <div v-if="userInformation.admin == 1" class="mode-toggle">
          <div class="pill-toggle">
            <div class="pill-slider" :class="{ 'pill-slider--right': viewMode === 'user' }"></div>
            <button class="pill-option mx-2" :class="{ 'pill-option--active': viewMode === 'admin' }"
              @click="viewMode = 'admin'">
              <v-icon size="14">mdi-shield-account-outline</v-icon>
              Admin
            </button>
            <button class="pill-option ml-4 " :class="{ 'pill-option--active': viewMode === 'user' }"
              @click="viewMode = 'user'">
              <v-icon size="14">mdi-account-outline</v-icon>
              Mon espace
            </button>
          </div>
        </div>

        <div class="top-bar-right">
          <v-btn icon size="small" variant="text" color="#3f51b5" @click="openPlanning" title="Voir le planning">
            <v-icon size="22">mdi-calendar-month-outline</v-icon>
          </v-btn>
          <span class="user-display-name">{{ displayName }}</span>
          <v-btn icon size="small" variant="text" color="#757575" @click="logout">
            <v-icon size="20">mdi-logout</v-icon>
          </v-btn>
        </div>
      </div>
      <!-- Views -->
      <HomeAdmin v-if="viewMode === 'admin'" :userInfo="userInformation" :allUsers="allUsers" :get_basic_data="get_basic_data"
        :congeTypes="congeTypes" />
      <HomeUser v-else :userInfo="userInformation" :congeTypes="congeTypes" @open-details="onOpenDetails"/>
      <!-- ═══ Planning dialog ═══ -->
      <v-dialog v-model="showPlanningDialog" fullscreen transition="dialog-bottom-transition">
        <v-card style="display:flex;flex-direction:column;height:100vh;background:#f9fafc;">

          <!-- Header -->
          <div class="planning-dialog-header">
            <div style="display:flex;align-items:center;gap:10px;flex-shrink:0;">
              <v-icon color="#3f51b5" size="22">mdi-calendar-month</v-icon>
              <span class="planning-dialog-title">Planning des congés</span>
            </div>

            <!-- Filtre employé -->
            <v-autocomplete v-model="planningSelectedEmployee" :items="planningEmployeeList" item-title="name"
              item-value="name" placeholder="Tous les employés" variant="outlined" density="compact" hide-details
              clearable rounded="lg" prepend-inner-icon="mdi-account-search-outline"
              style="max-width:220px;min-width:160px;" />

            <!-- Filtres statut (admin seulement) -->
            <div v-if="viewMode === 'admin'" class="planning-legend">
              <v-chip size="small" :variant="planningFilterStatus === null ? 'flat' : 'tonal'" color="#424242"
                @click="planningFilterStatus = null" style="cursor:pointer;">
                Tous
              </v-chip>
              <v-chip size="small" :variant="planningFilterStatus === 0 ? 'flat' : 'tonal'" color="#fb8c00"
                @click="planningFilterStatus = planningFilterStatus === 0 ? null : 0" style="cursor:pointer;">
                <v-icon start size="12">mdi-clock-outline</v-icon>En attente
              </v-chip>
              <v-chip size="small" :variant="planningFilterStatus === 1 ? 'flat' : 'tonal'" color="#43a047"
                @click="planningFilterStatus = planningFilterStatus === 1 ? null : 1" style="cursor:pointer;">
                <v-icon start size="12">mdi-check-circle-outline</v-icon>Validées
              </v-chip>
              <v-chip size="small" :variant="planningFilterStatus === 2 ? 'flat' : 'tonal'" color="#e53935"
                @click="planningFilterStatus = planningFilterStatus === 2 ? null : 2" style="cursor:pointer;">
                <v-icon start size="12">mdi-close-circle-outline</v-icon>Refusées
              </v-chip>
            </div>

            <v-btn icon variant="text" color="#757575" size="small" class="ml-auto" @click="showPlanningDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Body -->
          <div class="planning-dialog-body">
            <div v-if="planningLoading" class="planning-loading">
              <v-progress-circular size="52" indeterminate color="#3f51b5"></v-progress-circular>
              <span>Chargement du planning...</span>
            </div>
            <FullCalendar v-else :options="calendarOptions" class="planning-calendar" />
            <!-- Type legend -->
            <div v-if="!planningLoading && congeTypes.length" class="planning-type-legend">
              <span class="planning-type-legend-label">Types :</span>
              <div v-for="type in planningTypeLegend" :key="type.label" class="planning-legend-item">
                <span class="planning-legend-dot" :style="{ background: type.color }"></span>
                <span class="planning-legend-text">{{ type.label }}</span>
              </div>
            </div>
          </div>

        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import engine from "@/core/index";
import HomeUser from "@/view/HomeUser.vue";
import HomeAdmin from "@/view/HomeAdmin.vue";
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';

export default {
  components: { HomeUser, HomeAdmin, FullCalendar },
  data() {
    return {
      isLoading: true,
      userInformation: {},
      allUsers: [],
      congeTypes: [],
      viewMode: "user",
      showPlanningDialog: false,
      planningEvents: [],
      planningLoading: false,
      planningFilterStatus: null,
      planningSelectedEmployee: null,
    };
  },
  computed: {
    displayName() {
      const u = this.userInformation;
      if (u.user_name) return u.user_name;
      if (u.user_email) return u.user_email;
      return "Utilisateur";
    },
    planningTypeLegend() {
      const colorMap = {
        red: '#e53935', blue: '#3f51b5', green: '#43a047',
        orange: '#fb8c00', yellow: '#f9a825', purple: '#9c27b0',
        gray: '#757575', grey: '#757575', pink: '#e91e63',
        teal: '#00838f', cyan: '#00acc1', indigo: '#3f51b5',
      };
      // Only show types that actually appear in current events
      const usedTypeIds = new Set(
        this.planningEvents.map(e => e.extendedProps && e.extendedProps.typeId).filter(Boolean)
      );
      return this.congeTypes
        .filter(t => usedTypeIds.size === 0 || usedTypeIds.has(t.id_conge_type))
        .map(t => ({
          label: t.libelle,
          color: colorMap[t.color] || t.color || '#3f51b5',
        }));
    },
    planningEmployeeList() {
      const names = new Set(
        this.planningEvents
          .map(e => e.extendedProps && e.extendedProps.employeeName)
          .filter(Boolean)
      );
      return Array.from(names).sort().map(n => ({ name: n }));
    },
    calendarOptions() {
      let filtered = this.planningFilterStatus !== null
        ? this.planningEvents.filter(e => e.extendedProps && e.extendedProps.statut == this.planningFilterStatus)
        : this.planningEvents;
      if (this.planningSelectedEmployee) {
        filtered = filtered.filter(e => e.extendedProps && e.extendedProps.employeeName === this.planningSelectedEmployee);
      }
      return {
        plugins: [dayGridPlugin, listPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        locale: frLocale,
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,listMonth',
        },
        buttonText: {
          today: "Aujourd'hui",
          month: 'Mois',
          list: 'Liste',
          prev: '‹',
          next: '›',
        },
        dayMaxEventsText: 'événement(s)',
        moreLinkText: (n) => `+${n} de plus`,
        events: filtered,
        height: 'calc(100vh - 110px)',
        dayMaxEvents: 4,
        eventClick: this.onPlanningEventClick,
      };
    },
  },
  mounted() {
    this.get_basic_data()
  },
  methods: {
    get_basic_data() {
      let that = this;
      let token = localStorage.getItem("userToken");

      engine.get_all_users(function (data) {
        if (data.code == 0) {
          that.allUsers = data.data.data.map(r => r.fieldData || r);
        }
      });
      engine.get_conge_type(function (data) {
        if (data.code == 0) {
          that.congeTypes = data.data.data.map(r => r.fieldData || r);
        }
      });
      engine.get_user_by_token(token, function (data) {
        if (data.code == 0) {
          that.userInformation = data.data.data.data[0].fieldData;
          that.viewMode = that.userInformation.admin == 1 ? "admin" : "user";
          that.isLoading = false;
        } else {
          that.$router.push("/login");
        }
      });

    },
    logout() {
      localStorage.setItem("userToken", undefined);
      this.$store.replaceState({});
      this.$router.push("/login");
    },
    onOpenDetails() { },

    // ── Planning ──
    fetchCongesForUser(userId) {
      return new Promise((resolve) => {
        engine.get_conge_by_user_id(userId, (data) => {
          if (data.code == 0) {
            resolve(data.data.data.data.map(v => v.fieldData));
          } else {
            resolve([]);
          }
        });
      });
    },
    buildPlanningEvent(conge, employeeName) {
      const colorMap = {
        red: '#e53935', blue: '#3f51b5', green: '#43a047',
        orange: '#fb8c00', yellow: '#f9a825', purple: '#9c27b0',
        gray: '#757575', grey: '#757575', pink: '#e91e63',
        teal: '#00838f', cyan: '#00acc1', indigo: '#3f51b5',
      };
      const statusIcons = { 0: '⏳', 1: '✓', 2: '✗', 3: '↩', 4: '🗑' };
      const type = this.congeTypes.find(t => t.id_conge_type === conge.id_conge_type);
      const typeName = type ? type.libelle : '—';
      const typeColor = type ? (colorMap[type.color] || type.color || '#3f51b5') : '#3f51b5';
      const color = typeColor;
      const statusPrefix = this.viewMode === 'admin' ? `${statusIcons[conge.statut] || ''} ` : '';
      const title = employeeName ? `${statusPrefix}${employeeName} — ${typeName}` : `${statusPrefix}${typeName}`;

      const startDate = new Date(parseInt(conge.debut_timestamp));
      const endDate = new Date(parseInt(conge.fin_timestamp));
      endDate.setDate(endDate.getDate() + 1); // FullCalendar end est exclusif

      return {
        title,
        start: startDate.toISOString().slice(0, 10),
        end: endDate.toISOString().slice(0, 10),
        backgroundColor: color,
        borderColor: color,
        textColor: 'white',
        extendedProps: {
          statut: conge.statut,
          employeeName: employeeName || '',
          typeName,
          typeId: conge.id_conge_type,
          nbrDays: conge.nbr_days,
        },
      };
    },
    async openPlanning() {
      this.planningEvents = [];
      this.planningFilterStatus = null;
      this.planningSelectedEmployee = null;
      this.planningLoading = true;
      this.showPlanningDialog = true;

      const results = await Promise.all(
        this.allUsers.map(user =>
          this.fetchCongesForUser(user.user_id).then(conges => ({ user, conges }))
        )
      );

      if (this.viewMode === 'admin') {
        this.planningEvents = results.flatMap(({ user, conges }) =>
          conges.map(c => this.buildPlanningEvent(c, user.user_name))
        );
      } else {
        this.planningEvents = results.flatMap(({ user, conges }) =>
          conges
            .filter(c => c.statut == 1)
            .map(c => this.buildPlanningEvent(c, user.user_name))
        );
      }

      this.planningLoading = false;
    },
    onPlanningEventClick(info) {
      info.jsEvent.preventDefault();
    },
  },
};
</script>

<style scoped>
.home-wrapper {
  min-height: 100vh;
  background: #f0f2f5;
}

.loading-screen {
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: #f0f2f5;
}

.loading-text {
  color: #3f51b5;
  font-weight: 500;
  font-size: 15px;
  margin: 0;
}

.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 64px;
  background: white;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 100;
}

.top-bar-brand {
  display: flex;
  align-items: center;
  gap: 8px;
}

.brand-name {
  font-size: 18px;
  font-weight: 800;
  color: #3f51b5;
  letter-spacing: 1px;
}

.brand-sep {
  color: #e0e0e0;
  font-size: 18px;
}

.brand-sub {
  font-size: 14px;
  color: #9e9e9e;
  font-weight: 400;
}

.mode-toggle {
  flex: 0 0 auto;
}

.pill-toggle {
  position: relative;
  display: flex;
  align-items: center;
  background: #f0f2f5;
  border-radius: 999px;
  padding: 3px;
  gap: 0;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.08);
}

.pill-slider {
  position: absolute;
  top: 3px;
  left: 3px;
  width: calc(50% - 3px);
  height: calc(100% - 6px);
  background: linear-gradient(135deg, #3f51b5, #283593);
  border-radius: 999px;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(63, 81, 181, 0.35);
}

.pill-slider--right {
  transform: translateX(100%);
}

.pill-option {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 999px;
  color: #9e9e9e;
  transition: color 0.2s;
  white-space: nowrap;
  outline: none;
}

.pill-option--active {
  color: white;
}

.top-bar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-display-name {
  font-size: 14px;
  font-weight: 600;
  color: #424242;
}

@media (max-width: 768px) {
  .top-bar {
    padding: 0 16px;
    flex-wrap: wrap;
    height: auto;
    padding-top: 10px;
    padding-bottom: 10px;
    gap: 8px;
  }

  .brand-sep,
  .brand-sub {
    display: none;
  }
}

/* ── Planning dialog ── */
.planning-dialog-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f0f0;
  background: white;
  flex-shrink: 0;
  flex-wrap: wrap;
  position: sticky;
  top: 0;
  z-index: 10;
}

.planning-dialog-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
}

.planning-legend {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.planning-dialog-body {
  flex: 1;
  overflow: auto;
  padding: 20px 24px;
}

.planning-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  gap: 16px;
  color: #757575;
  font-size: 14px;
}

.planning-calendar {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(63, 81, 181, 0.07);
}

.planning-type-legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
  padding: 10px 16px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(63, 81, 181, 0.06);
}

.planning-type-legend-label {
  font-size: 12px;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 4px;
}

.planning-legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.planning-legend-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  flex-shrink: 0;
}

.planning-legend-text {
  font-size: 12px;
  color: #424242;
  font-weight: 500;
}
</style>
